import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from '../networking/axiosconfig';
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { jwtDecode } from "jwt-decode";
export default function LoginScreen() {

  const [error, setError] = useState("");

  const [email, setEmail] = useState(() => sessionStorage.getItem('loginEmail') || '');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [loginError, setLoginError] = useState(() => sessionStorage.getItem('loginError') || '');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [passwordVisible, setPasswordVisible] = useState(false);

  useEffect(() => {
    sessionStorage.setItem('loginEmail', email);
  }, [email]);

  // Purge any plaintext password stored by an earlier build of this page.
  useEffect(() => {
    sessionStorage.removeItem('loginPassword');
  }, []);

  useEffect(() => {
    if (loginError) {
      sessionStorage.setItem('loginError', loginError);
    } else {
      sessionStorage.removeItem('loginError');
    }
  }, [loginError]);

  useEffect(() => {
    getOrganisationDetail();
  }, []);

  const getOrganisationDetail = async () => {
    try {
      const res = await axiosInstance.get('/api/organization/branding');
      if (res.data && res.data.status && res.data.data) {
        setUserData(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching organization branding:", err);
    }
  };

  const validate = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async () => {
    // Only clear previous errors if validation succeeds
    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post(`/api/auth/login`, {
        email,
        password,
      });

      if (response.data?.data?.token) {
        const { token, userId, first_name, last_name, is_active, user_profile_img } = response.data.data;

        if (is_active === 1) {
          setLoginError("Your account is inactive. Please contact the administrator.");
          return;
        }

        const decodedToken = jwtDecode(token);
        const userRole = Number(decodedToken.role);

        localStorage.setItem("token", token);
        localStorage.setItem("userId", userId);
        localStorage.setItem("first_name", first_name);
        localStorage.setItem("last_name", last_name);
        localStorage.setItem("role", userRole);
        if (user_profile_img) {
          localStorage.setItem("user_profile_img", user_profile_img);
        }

        // Clear session storage on successful login
        sessionStorage.removeItem('loginEmail');
        sessionStorage.removeItem('loginPassword');
        sessionStorage.removeItem('loginError');
        setLoginError('');

        if (userRole === 1 || userRole === 2) {
          navigate("/dashboard", { replace: true });
        } else if (userRole === 3) {
          navigate("/employee-attendance", { replace: true });
        } else {
          console.error("Unexpected role received:", userRole);
          setLoginError(`Unauthorized role: ${userRole}. Contact administrator.`);
        }
      } else {
        setLoginError("Invalid Email or Password");
      }
    } catch (error) {
      console.error("Login error:", error);
      setLoginError("Invalid Email or Password");
    } finally {
      setLoading(false);
    }
  };

  const clearLoginError = () => {
    setLoginError('');
    sessionStorage.removeItem('loginError');
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible((prevState) => !prevState);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-indigo-100 px-4">
      <div className="w-full max-w-md space-y-8 bg-white p-6 shadow-lg rounded-2xl">
        <div className="flex items-center flex-col">
          {userData?.organization_logo ? (
            <img
              alt="Company Logo"
              src={userData.organization_logo}
              className="w-30 sm:w-32 md:w-36 lg:w-40 h-auto"
            />
          ) : (
            <p className="text-gray-500">Loading logo...</p>
          )}
          <h2 className="xl:text-2xl text-xl font-bold text-center text-gray-900">
            Sign in to your account
          </h2>
        </div>


        <div className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                // Only clear validation errors, not login errors
                if (errors.email) {
                  setErrors({ ...errors, email: '' });
                }
              }}
              onFocus={() => {
                // Clear login error when user focuses on input fields - intentional interaction
                if (loginError) {
                  clearLoginError();
                }
              }}
              className="mt-1 block w-full rounded-lg border border-gray-300 p-2.5 text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>

          <div className="relative">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <input
              id="password"
              type={passwordVisible ? "text" : "password"}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                // Only clear validation errors, not login errors
                if (errors.password) {
                  setErrors({ ...errors, password: '' });
                }
              }}
              onFocus={() => {
                // Clear login error when user focuses on input fields - intentional interaction
                if (loginError) {
                  clearLoginError();
                }
              }}
              className="mt-1 block w-full rounded-lg border border-gray-300 p-2.5 text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute inset-y-0 right-3 top-6 flex items-center"
            >
              {passwordVisible ? (
                <AiOutlineEyeInvisible size={20} />
              ) : (
                <AiOutlineEye size={20} />
              )}
            </button>
            {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
          </div>
          {/* Display login error message with a close button */}
          {loginError && (
            <div className="text-red-500 rounded relative">
              <span className="block sm:inline">{loginError}</span>

            </div>
          )}

          <button
            type="button"
            onClick={handleLogin}
            className="w-full rounded-lg bg-blue-600 hover:bg-blue-500 py-2 text-white font-semibold transition-all"
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center justify-center space-x-2">
                <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  />
                </svg>
                <span>Signing in...</span>
              </div>
            ) : (
              "Sign in"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
